export const queryKeys = {
  cases: {
    all: ['cases'] as const,
    detail: (id: string) => ['cases', id] as const,
    byFlow: (flow: string) => ['cases', 'by-flow', flow] as const,
    byInvestor: (investorId: string) => ['cases', 'by-investor', investorId] as const,
    mine: ['cases', 'mine'] as const,
    // Namespaced under 'real' so the still-mock keys above (used by the deferred investor
    // dashboard, Phase 9) can never collide with real backend data under what would otherwise
    // look like the same query key.
    list: (params: unknown) => ['cases', 'real', 'list', params] as const,
    // Fixed prefix for documentation/type purposes — actual list invalidation after a write uses
    // a predicate matching this prefix (`use-case-writes.ts`), since `list()` embeds the full
    // filter object and there's no single exact key for "every open cases list".
    listAll: ['cases', 'real', 'list'] as const,
    real: (id: string) => ['cases', 'real', id] as const,
    holdings: (id: string) => ['cases', 'real', id, 'holdings'] as const,
    documents: (id: string) => ['cases', 'real', id, 'documents'] as const,
    uploadLinks: (id: string) => ['cases', 'real', id, 'upload-links'] as const,
    trades: (id: string, page: number, pageSize: number) =>
      ['cases', 'real', id, 'trades', page, pageSize] as const,
    prescriptionSteps: (id: string) => ['cases', 'real', id, 'prescription-steps'] as const,
    goals: (id: string) => ['cases', 'real', id, 'goals'] as const,
    snapshots: (id: string) => ['cases', 'real', id, 'snapshots'] as const,
    events: (id: string, page: number, pageSize: number) =>
      ['cases', 'real', id, 'events', page, pageSize] as const,
    preview: (id: string) => ['cases', 'real', id, 'preview'] as const,
  },
  holdings: {
    byCase: (caseId: string) => ['holdings', caseId] as const,
  },
  trades: {
    byCase: (caseId: string) => ['trades', caseId] as const,
  },
  documents: {
    byCase: (caseId: string) => ['documents', caseId] as const,
  },
  prescriptionSteps: {
    byCase: (caseId: string) => ['prescription-steps', caseId] as const,
  },
  calls: {
    list: (params: unknown) => ['calls', 'real', 'list', params] as const,
    status: (callId: string) => ['calls', 'real', 'status', callId] as const,
  },
  session: {
    me: ['me'] as const,
    meStats: ['me', 'stats'] as const,
  },
  admin: {
    users: {
      // Shared prefix — mutations invalidate this so every paginated page of `list` refetches,
      // not just whichever page happens to be open (`queryKeys.cases.list`'s exact same pattern).
      all: ['admin-users'] as const,
      list: (params: unknown) => ['admin-users', 'list', params] as const,
      auditLog: (userId: string) => ['admin-users', 'audit-log', userId] as const,
      lastSyncRun: ['admin-users', 'last-sync-run'] as const,
    },
    overview: (params: unknown) => ['admin', 'overview', params] as const,
  },
  // Real as of Phase 10. Replaces the old `list(audience, recipientId)` shape — the real endpoint
  // auto-scopes to the caller from the auth token, so there's nothing client-side to key by.
  notifications: {
    list: (params: unknown) => ['notifications', 'list', params] as const,
    unreadCount: ['notifications', 'unread-count'] as const,
  },
  investorsResource: {
    list: (params: unknown) => ['investors-resource', 'list', params] as const,
    detail: (id: string) => ['investors-resource', id] as const,
    reveal: (id: string) => ['investors-resource', id, 'reveal'] as const,
    cases: (id: string) => ['investors-resource', id, 'cases'] as const,
    me: ['investors-resource', 'me'] as const,
    myCases: ['investors-resource', 'me', 'cases'] as const,
  },
  recommendations: {
    byInvestor: (investorId: string) => ['recommendations', investorId] as const,
    mine: ['recommendations', 'me'] as const,
  },
  public: {
    tracking: (token: string) => ['public', 'tracking', token] as const,
    uploadLink: (token: string) => ['public', 'upload-link', token] as const,
  },
  marketData: {
    ticker: ['market-data', 'ticker'] as const,
  },
} as const
